import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import { getIsDesktop } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import sortBy from 'lodash/sortBy';
import stubFalse from 'lodash/stubFalse';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  FEATURES_SECTION_ID,
  LOCATION_SECTION_ID,
  PRICING_SECTION_ID,
  REVIEWS_SECTION_ID,
} from '@bridebook/web/app-shared/components/supplier/sections/ids';
import { StickyNavigationBar } from '@bridebook/web/app-shared/components/supplier/sections/sticky-navigation-bar/sticky-navigation-bar';

type TStickyTabId =
  | typeof FEATURES_SECTION_ID
  | typeof PRICING_SECTION_ID
  | typeof REVIEWS_SECTION_ID
  | typeof LOCATION_SECTION_ID;

type TTabs = Array<{ id: TStickyTabId; text: string }>;

// Fixed offset from top edge of the screen at which we activate the incoming tab.
const TOP_OFFSET_THRESHOLD_IN_PX = 50;

const ANALYTICS_CTA_BY_ID: Record<TStickyTabId, string> = {
  [FEATURES_SECTION_ID]: 'Overview',
  [PRICING_SECTION_ID]: 'Pricing',
  [REVIEWS_SECTION_ID]: 'Review',
  [LOCATION_SECTION_ID]: 'Location',
};

/* TODO: either upgrade react-sticky to 6.0.3 or find a suitable replacement
      using a StickyContainer within another causes the inner children's style and behaviour
      to be influenced by the outer containers and these containers' children */

// This is the height of a different Sticky component that appears before this one in the DOM
// And as such it applies an offset to Sticky components appearing later despite not influencing them at all
const FIXME_STICKY_BROCHURE_REQUEST_HEIGHT_OFFSET = 68;
// This overrides the 68 pixels added to the top property due to StickyBrochureRequest simply existing in the DOM.
const FIXME_STICKY_BROCHURE_REQUEST_STYLE_OVERRIDE = { top: 0 };

export const StickyTabs: FC = () => {
  const { t } = useTranslation('supplier');
  const dispatch = useDispatch();
  const isDesktop = useSelector(getIsDesktop);

  // if the user ever changes resolution to mobile, so that the Sticky Brochure Request appears
  // it permanently applies its offset to this Sticky
  // even if you return to desktop view and the Sticky Brochure Request is gone
  const [wasNotDesktopAtAnyPoint, setWasNotDesktopAtAnyPoint] = useState(!isDesktop);

  const [existingFeatures, setExistingFeatures] = useState<Record<TStickyTabId, boolean>>(
    () => mapValues(ANALYTICS_CTA_BY_ID, stubFalse) as Record<TStickyTabId, boolean>,
  );
  const [order, setOrder] = useState<string>('');

  // recalculate values from document, preventing updates if new feature set is deeply equal with the old one
  // there are no listeners that we can rely on during this process
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // this cannot be a simple read and set due to causing a ReferenceError: document is not defined on vercel
    const reloadedExistingFeaturesDict = mapValues<typeof ANALYTICS_CTA_BY_ID>(
      ANALYTICS_CTA_BY_ID,
      (_ignored: string, key: TStickyTabId) => !!document?.getElementById(key),
    ) as Record<TStickyTabId, boolean>;

    // check for item order differences in case of items changing section position (such as reviews being put as first on mobile)
    const newOrder = sortBy(Object.keys(ANALYTICS_CTA_BY_ID), (id) =>
      Number(document.getElementById(id)?.getBoundingClientRect().top),
    ).join();

    // check equality to skip setting if objects are deeply equal, otherwise we land in a constant rerender loop
    if (isEqual(existingFeatures, reloadedExistingFeaturesDict) && newOrder === order) return;
    setOrder(newOrder);
    setExistingFeatures(reloadedExistingFeaturesDict);
  });

  const tabs: TTabs = useMemo(() => {
    const existingTabs = compact<TTabs[number]>([
      existingFeatures[FEATURES_SECTION_ID] && {
        id: FEATURES_SECTION_ID,
        text: t('tabs.featuresSection'),
      },
      existingFeatures[PRICING_SECTION_ID] && {
        id: PRICING_SECTION_ID,
        text: t('tabs.pricingSection'),
      },
      existingFeatures[REVIEWS_SECTION_ID] && {
        id: REVIEWS_SECTION_ID,
        text: t('tabs.supplierTestimonialsSection'),
      },
      existingFeatures[LOCATION_SECTION_ID] && {
        id: LOCATION_SECTION_ID,
        text: t('tabs.supplierLocationSection'),
      },
    ]);
    const orderDict = Object.fromEntries(
      order.split(',').map((id, index) => [id, index]),
    ) as Partial<Record<TStickyTabId, number>>;
    return sortBy(existingTabs, (tab) => orderDict[tab.id]);
  }, [existingFeatures, order, t]);

  useEffect(() => {
    if (!isDesktop) setWasNotDesktopAtAnyPoint(true);
  }, [wasNotDesktopAtAnyPoint, isDesktop]);

  const sendClickedAnalytics = useCallback(
    (id: TStickyTabId) => {
      dispatch(
        clickedOnLoggedOutPage({
          clickedLocation: 'supplierProfile',
          clickedSection: 'autoscrollNavigationBar',
          cta: ANALYTICS_CTA_BY_ID[id],
        }),
      );
    },
    [dispatch],
  );

  return (
    <StickyNavigationBar
      allowOverrides={wasNotDesktopAtAnyPoint}
      disableCompensation
      manuallyCalculateOffset
      onClick={sendClickedAnalytics}
      // desktop does not use StickyBrochureRequest and as such does not need the adjustments
      // unless you reduce browser size to the point where you're not considered a desktop anymore
      stickyStyle={FIXME_STICKY_BROCHURE_REQUEST_STYLE_OVERRIDE}
      tabActivationThreshold={TOP_OFFSET_THRESHOLD_IN_PX}
      tabs={tabs}
      topOffset={FIXME_STICKY_BROCHURE_REQUEST_HEIGHT_OFFSET}
    />
  );
};
